// @flow

import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import styled from '@emotion/styled';

type Props = {
  acceptedFile?: Object,
  onDrop: (acceptedFiles: Array<Object>, rejectedFiles: Array<Object>) => void,
  displayRejectedMessage?: boolean
};

const defaultProps = {
  acceptedFile: undefined,
  displayRejectedMessage: false
};

const ImagePreview = styled.div(({ src }) => ({
  backgroundImage: `url(${src})`,
  width: 100,
  height: 100,
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  backgroundColor: 'black',
  borderRadius: 3
}));

const StyledDropzone = styled.div(({ isDragActive, theme }) => ({
  borderColor: isDragActive ? theme.color.primary : theme.color.basis4,
  borderRadius: 5,
  borderStyle: isDragActive ? 'solid' : 'dashed',
  padding: theme.spacing.small,
  textAlign: 'center'
}));

const DropzoneContent = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

const DropZoneMainText = styled.span(props => ({
  color: props.theme.color.basis4
}));

const DropZonePreviewContainer = styled.div({
  flexGrow: 1
});

const DropZoneTextContainer = styled.div({
  flex: 3
});

const DropZoneRejectedText = styled.span(({ theme }) => ({
  color: theme.color.danger
}));

const Label = styled(({ children, ...props }) => (
  <label htmlFor="document" {...props}>
    {children}
  </label>
))(({ theme }) => ({
  display: 'block',
  fontSize: theme.fontSize.xsmall,
  marginBottom: theme.spacing.xsmall,
  textTransform: 'uppercase',
  width: '100%'
}));

const ExtensionLabel = styled.span({
  paddingLeft: 6
});

function DocumentDropZoneField({ acceptedFile, onDrop, displayRejectedMessage }: Props) {
  const onDropCallback = React.useCallback(onDrop, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ['.pdf', '.png', '.jpg', '.jpeg'],
    maxSize: 5000000,
    multiple: false,
    onDrop: onDropCallback
  });

  return (
    <div>
      <Label>
        UPLOAD A CLOSE UP OF YOUR BANK STATEMENT WHICH SHOWS FULL NAME, ADDRESS, BSB AND ACCOUNT
        NUMBER. ALL INFORMATION MUST BE DISPLAYED WITHIN THE ONE SCREENSHOT OR DOCUMENT UPLOADED.
      </Label>
      <StyledDropzone {...getRootProps({ isDragActive })}>
        <input {...getInputProps()} />
        {acceptedFile ? (
          <DropzoneContent>
            <DropZonePreviewContainer>
              <ImagePreview src={acceptedFile.preview} />
            </DropZonePreviewContainer>
            <DropZoneTextContainer>{acceptedFile.name}</DropZoneTextContainer>
          </DropzoneContent>
        ) : (
          <DropZoneTextContainer>
            <div>
              <DropZoneMainText>Click or drop files here</DropZoneMainText>
              <ExtensionLabel>(jpg, png, pdf)</ExtensionLabel>
            </div>
          </DropZoneTextContainer>
        )}
      </StyledDropzone>
      {displayRejectedMessage && (
        <DropZoneRejectedText>
          Cannot upload this file. It is either too large or not an accepted file type.
        </DropZoneRejectedText>
      )}
    </div>
  );
}

DocumentDropZoneField.defaultProps = defaultProps;

export default DocumentDropZoneField;
